<template>
  <div class="max-w-4xl mx-auto pb-10 relative animate-fade-in">

    <!-- ENCABEZADO PRINCIPAL -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <ShieldAlert class="w-6 h-6 text-brand-600" />
          Cálculo Diario de Reservas de Cartera
        </h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm">
          Evaluación automática de días de atraso, deterioro y registro contable de provisiones por sucursal.
        </p>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-12 gap-6">

      <!-- COLUMNA IZQUIERDA: EXPLICACIÓN Y NORMATIVA -->
      <div class="md:col-span-5 space-y-6">
        <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <h3 class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            ¿Cómo funciona este proceso?
          </h3>
          <p class="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
            El sistema escanea la cartera activa con interés <strong>Normal / Sobre Saldo</strong> de la sucursal seleccionada y calcula los días de mora con respecto a la fecha actual.
          </p>
          <ul class="text-xs text-slate-600 dark:text-slate-300 space-y-2 list-disc pl-4">
            <li>Actualiza la clasificación de riesgo (<strong>A1 hasta E</strong>).</li>
            <li>Calcula el porcentaje de reserva exigido según normativa (Vivienda, Consumo, Empresa).</li>
            <li>Obtiene la variación neta del día para no duplicar montos.</li>
            <li>Genera y mayoriza la partida de diario en contabilidad.</li>
          </ul>
        </div>

        <!-- RECUADRO DE AVISO -->
        <div class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-2xl flex items-start gap-3">
          <Info class="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div class="text-xs text-blue-800 dark:text-blue-300 space-y-1">
            <span class="font-bold block">Recomendación operativa</span>
            <span>Ejecute este proceso al finalizar el día o antes de generar los estados financieros mensuales.</span>
          </div>
        </div>
      </div>

      <!-- COLUMNA DERECHA: FORMULARIO DE EJECUCIÓN -->
      <div class="md:col-span-7">
        <div class="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">

          <div>
            <label class="text-xs font-semibold text-slate-500 uppercase mb-2 block">
              1. Seleccionar Sucursal a Evaluar
            </label>
            <select
              v-model="selectedBranch"
              :disabled="isRunning"
              class="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-700 dark:text-slate-200 disabled:opacity-60"
            >
              <option value="" disabled>Seleccione una sucursal...</option>
              <option v-for="b in branches" :key="b.id" :value="b.id">
                {{ b.name }}
              </option>
            </select>
          </div>

          <div class="pt-2">
            <button
              @click="executeProvision"
              :disabled="!selectedBranch || isRunning"
              type="button"
              class="w-full py-3.5 px-6 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99]"
            >
              <Loader2 v-if="isRunning" class="w-5 h-5 animate-spin" />
              <Play v-else class="w-5 h-5" />
              {{ isRunning ? 'Evaluando cartera y calculando...' : 'Ejecutar Cálculo de Reservas' }}
            </button>
          </div>

          <!-- RESULTADOS DE LA ÚLTIMA EJECUCIÓN -->
          <div
            v-if="resultSummary"
            class="mt-6 p-5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl space-y-4 animate-fade-in"
          >
            <div class="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-3">
              <div class="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-bold text-sm">
                <CheckCircle class="w-5 h-5" />
                <span>Cálculo procesado con éxito</span>
              </div>
              <span class="text-[10px] font-mono text-slate-400">{{ currentTime }}</span>
            </div>

            <div class="grid grid-cols-2 gap-4 text-xs">
              <div class="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700/60">
                <span class="text-slate-400 block text-[10px] uppercase font-bold mb-1">Créditos Evaluados</span>
                <span class="font-bold text-slate-800 dark:text-slate-100 text-lg">
                  {{ resultSummary.loans_processed }}
                </span>
              </div>

              <div class="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700/60">
                <span class="text-slate-400 block text-[10px] uppercase font-bold mb-1">Variación Contable Neta</span>
                <span
                  class="font-mono font-bold text-lg"
                  :class="resultSummary.net_provision_adjustment >= 0 ? 'text-brand-600 dark:text-brand-400' : 'text-emerald-600'"
                >
                  ${{ Number(resultSummary.net_provision_adjustment).toLocaleString('en-US', { minimumFractionDigits: 2 }) }}
                </span>
              </div>
            </div>

            <p class="text-[11px] text-slate-500 text-center italic">
              {{ resultSummary.net_provision_adjustment === 0
                ? 'No hubo variaciones en los días de mora; no fue necesario generar un nuevo asiento.'
                : 'Se ha registrado la partida de ajuste automáticamente en el Libro Diario.'
              }}
            </p>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ShieldAlert, Info, Play, Loader2, CheckCircle } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const { showSuccess, showError } = useToast();

const branches = ref([]);
const selectedBranch = ref('');
const isRunning = ref(false);
const resultSummary = ref(null);
const currentTime = ref('');

// Cargar sucursales de la empresa
const fetchBranches = async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user?.company) return;
  try {
    const res = await apiClient.get(`/branches?company=${user.company}`);
    branches.value = Array.isArray(res.data) ? res.data : (res.data.data || []);

    // Seleccionar por defecto la sucursal del usuario
    if (user.branch_id) {
      selectedBranch.value = user.branch_id;
    } else if (branches.value.length === 1) {
      selectedBranch.value = branches.value[0].id;
    }
  } catch (e) {
    console.error('Error al cargar sucursales:', e);
    showError('No se pudieron cargar las sucursales.');
  }
};

onMounted(fetchBranches);

// Ejecutar el proceso diario al backend
const executeProvision = async () => {
  if (!selectedBranch.value) return;

  isRunning.value = true;
  resultSummary.value = null;

  try {
    const user = SecureStorage.getItem('llevo_user');
    const nowDate = new Date();
    const year = nowDate.getFullYear();
    const month = String(nowDate.getMonth() + 1).padStart(2, '0');
    const day = String(nowDate.getDate()).padStart(2, '0');
    const browserDate = `${year}-${month}-${day}`;
    const payload = {
      company_id: user.company,
      branch_id: selectedBranch.value,
      date: browserDate
    };

    const response = await apiClient.post('/run_daily_provisions', payload);
    resultSummary.value = response.data;

    // Guardamos la hora en que se ejecutó para mostrar en la tarjeta de resultados
    const now = new Date();
    currentTime.value = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    showSuccess('Cálculo diario de reservas finalizado con éxito.');
  } catch (error) {
    console.error(error);
    showError(error.response?.data?.error || 'Error al procesar el cálculo diario de reservas.');
  } finally {
    isRunning.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in { animation: fade-in 0.25s ease-out forwards; }
</style>